import * as React from 'react';
import { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import { tokens } from '../../../../theme';
import ConnString from '../../../../ConnString.js';

const PRINT_STYLE_ID = 'liquidation-report-print-style';
const REPORT_COLUMNS = ['Docdate', 'Docnumber', 'Status', 'RefNo', 'TradeName','Remarks',  'Amount', 'Applied', 'Balance'];
const NUMERIC_COLUMNS = new Set(['Amount', 'Applied', 'Balance']);
const LIQUIDATION_DETAIL_FROM_DATE = '2021-01-01';

const injectPrintStyle = () => {
  if (document.getElementById(PRINT_STYLE_ID)) {
    return;
  }

  const style = document.createElement('style');
  style.id = PRINT_STYLE_ID;
  style.innerHTML = `
    @page {
      size: A4 landscape;
      margin: 8mm;
    }

    @media print {
      body * { visibility: hidden; }
      #liquidation-printable, #liquidation-printable * { visibility: visible; }
      #liquidation-printable { position: absolute; left: 0; top: 0; width: 100%; padding: 8px; }
      .liquidation-no-print { display: none !important; }
      .liquidation-print-header { display: block !important; visibility: visible; font-size: 14px; font-weight: bold; margin-bottom: 4px; }
      .liquidation-print-meta { display: block !important; visibility: visible; font-size: 10px; color: #555; margin-bottom: 10px; }
      table {
        border-collapse: collapse;
        width: 100%;
        table-layout: fixed;
        font-size: 9px;
      }
      th, td {
        border: 1px solid #ccc;
        padding: 2px 4px;
        white-space: normal !important;
        word-break: break-word;
        overflow-wrap: anywhere;
        line-height: 1.15;
        vertical-align: top;
      }
      th { background-color: #2563eb !important; color: #fff !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .liquidation-total-row td { font-weight: bold; background-color: #e8eeff !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    }
  `;
  document.head.appendChild(style);
};

const Connstring2 = ConnString.connstring;
const numberFormatter = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const getTodayValue = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = `${today.getMonth() + 1}`.padStart(2, '0');
  const day = `${today.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const isNumericLike = (value) => {
  if (value === null || value === undefined || value === '') {
    return true;
  }

  if (typeof value === 'number') {
    return Number.isFinite(value);
  }

  if (typeof value === 'string') {
    return /^-?\d+(\.\d+)?$/.test(value.replace(/,/g, '').trim());
  }

  return false;
};

const getNumericValue = (value) => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value === 'string' && /^-?\d+(\.\d+)?$/.test(value.replace(/,/g, '').trim())) {
    const parsed = Number(value.replace(/,/g, '').trim());
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
};

const formatCellValue = (value, columnName = '') => {
  if (value === null || value === undefined || value === '') {
    return '—';
  }

  if (columnName === 'Status') {
    return String(value);
  }

  const numericValue = NUMERIC_COLUMNS.has(columnName) ? getNumericValue(value) : null;
  if (numericValue !== null) {
    return numberFormatter.format(numericValue);
  }

  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }

  if (typeof value === 'string') {
    const maybeDate = new Date(value);
    if (!Number.isNaN(maybeDate.getTime()) && /^\d{4}-\d{2}-\d{2}/.test(value)) {
      return maybeDate.toLocaleDateString();
    }

    return value;
  }

  return String(value);
};

const downloadFile = (content, fileName, mimeType) => {
  const blob = new Blob([content], { type: mimeType });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

const parseDateValue = (value) => {
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const getColumnKey = (availableColumns, desiredColumn) => {
  const desiredLower = desiredColumn.toLowerCase();
  return availableColumns.find((column) => String(column).toLowerCase() === desiredLower) || null;
};

const getFieldValue = (row, candidates) => {
  if (!row || typeof row !== 'object') {
    return '';
  }

  const keyMap = Object.keys(row).reduce((accumulator, key) => {
    accumulator[key.toLowerCase()] = key;
    return accumulator;
  }, {});

  for (const candidate of candidates) {
    const key = keyMap[String(candidate).toLowerCase()];
    if (!key) {
      continue;
    }

    const value = row[key];
    if (value !== null && value !== undefined && String(value).trim() !== '') {
      return value;
    }
  }

  return '';
};

const formatDateForReport = (value) => {
  if (!value) {
    return '';
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return String(value);
  }

  return parsed.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: '2-digit',
  });
};

const escapeHtml = (value) => {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
};

const buildLiquidationPrintHtml = ({
  companyName,
  cutoff,
  sourceRow,
  detailRows,
}) => {
  const firstRow = detailRows[0] || {};
  const name = getFieldValue(firstRow, ['Name', 'TradeName']) || getFieldValue(sourceRow, ['TradeName']);
  const docNo = getFieldValue(firstRow, ['DocNo', 'Docnumber']) || getFieldValue(sourceRow, ['Docnumber']);
  const docDate = getFieldValue(firstRow, ['DocDate', 'Date', 'Docdate']) || getFieldValue(sourceRow, ['Docdate']);
  const account = getFieldValue(firstRow, ['Account', 'AccountTitle']) || getFieldValue(sourceRow, ['Account']);
  const remarks = getFieldValue(firstRow, ['Remarks', 'Particulars']) || getFieldValue(sourceRow, ['Remarks']);

  const amountAdv = getFieldValue(firstRow, ['AmountAdv', 'Amount_Adv', 'AdvanceAmount']) || getFieldValue(sourceRow, ['Amount']);
  const liqAmount = getFieldValue(firstRow, ['LiqAmount', 'Liq_Amount', 'LiquidatedAmount']) || getFieldValue(sourceRow, ['Applied']);
  const balance = getFieldValue(firstRow, ['Balance']) || getFieldValue(sourceRow, ['Balance']);

  const detailHtml = detailRows.map((row) => {
    const detailDate = getFieldValue(row, ['AppliedDate']);
    const detailDocNo = getFieldValue(row, ['AppliedDoc']);
    const detailRefNo = getFieldValue(row,['AppliedRefNo']);
    const detailName = getFieldValue(row, ['AppliedAgentName']);
    const detailParticulars = getFieldValue(row, ['AppliedParticulars']);
    const detailAmount = getFieldValue(row, ['AppliedAmount']);
    const detailBalance = getFieldValue(row, ['RunningBalance', 'Balance']);

    return `
      <tr>
        <td>${escapeHtml(detailDate)}</td>
        <td>${escapeHtml(detailDocNo)}</td>
        <td>${escapeHtml(detailRefNo)}</td>
        <td>${escapeHtml(detailName)}</td>
        <td>${escapeHtml(detailParticulars)}</td>
        <td class="num">${escapeHtml(detailAmount)}</td>
        <td class="num">${escapeHtml(detailBalance)}</td>
      </tr>
    `;
  }).join('');

  const totalLiquidated = detailRows.reduce((sum, row) => sum + (getNumericValue(getFieldValue(row, ['AppliedAmount'])) ?? 0), 0);

  return `
<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>Liquidation Report</title>
    <style>
      body {
        margin: 24px;
        font-family: Arial, Helvetica, sans-serif;
        color: #111827;
      }
      .wrapper {
        border: 2px solid #d1d5db;
        padding: 20px;
      }
      .company {
        font-size: 20px;
        font-weight: 800;
        line-height: 1.25;
      }
      .tin {
        margin-top: 4px;
        font-size: 16px;
        font-weight: 700;
      }
      .title {
        margin-top: 24px;
        text-align: center;
        font-size: 34px;
        font-weight: 700;
      }
      .period {
        text-align: center;
        margin-top: 6px;
        font-size: 16px;
      }
      .meta {
        margin-top: 18px;
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 16px;
      }
      .meta table {
        width: 100%;
      }
      .meta td {
        padding: 3px 0;
        font-size: 16px;
      }
      .meta td:first-child {
        width: 120px;
        font-weight: 700;
      }
      .line {
        border-bottom: 1px solid #9ca3af;
        min-width: 180px;
        display: inline-block;
      }
      .num {
        text-align: right;
      }
      .detail {
        margin-top: 14px;
        width: 100%;
        border-collapse: collapse;
      }
      .detail th,
      .detail td {
        border-top: 1px solid #d1d5db;
        border-bottom: 1px solid #e5e7eb;
        padding: 6px 8px;
        font-size: 14px;
      }
      .detail th {
        text-align: left;
        font-weight: 700;
      }
      .total {
        margin-top: 8px;
        text-align: right;
        font-size: 22px;
        font-weight: 700;
      }
    </style>
  </head>
  <body>
    <div class="wrapper">
      <div class="company">${escapeHtml(companyName || '')}</div>
      <div class="title">LIQUIDATION REPORT</div>
      <div class="period">Covering period from ${escapeHtml(formatDateForReport(LIQUIDATION_DETAIL_FROM_DATE))} to ${escapeHtml(formatDateForReport(cutoff))}</div>

      <div class="meta">
        <table>
          <tr><td>DATE:</td><td><span class="line">${escapeHtml(formatDateForReport(docDate))}</span></td></tr>
          <tr><td>DOC No.</td><td><span class="line">${escapeHtml(docNo)}</span></td></tr>
          <tr><td>NAME</td><td><span class="line">${escapeHtml(name)}</span></td></tr>
          <tr><td>ACCOUNT</td><td><span class="line">${escapeHtml(account)}</span></td></tr>
          <tr><td>REMARKS</td><td><span class="line">${escapeHtml(remarks)}</span></td></tr>
        </table>
        <table>
          <tr><td>Amount Adv</td><td class="num"><span class="line">${escapeHtml(formatCellValue(amountAdv, 'Amount'))}</span></td></tr>
          <tr><td>Liq. Amount</td><td class="num"><span class="line">${escapeHtml(formatCellValue(liqAmount, 'Applied'))}</span></td></tr>
          <tr><td>Balance</td><td class="num"><span class="line">${escapeHtml(formatCellValue(balance, 'Balance'))}</span></td></tr>
        </table>
      </div>

     <table class="detail">
        <thead>
          <tr>
            <th>Date</th>
            <th>Doc No.</th>
            <th>Ref No</th>
            <th>Name</th>
            <th>Particulars</th>
            <th class="num">Amount</th>
            <th class="num">B alance</th>
          </tr>
        </thead>
        <tbody>
          ${detailHtml || '<tr><td colspan="7">No detail rows returned.</td></tr>'}
        </tbody>
      </table>

      <div class="total">Total Liquidated Amount&nbsp;&nbsp;${escapeHtml(numberFormatter.format(totalLiquidated))}</div>
    </div>
  </body>
</html>
  `;
};

const buildLiquidationSummaryPrintHtml = ({
  companyName,
  cutoff,
  tradeNameFilter,
  columns,
  groupedRows,
}) => {
  const renderSectionRows = (rows) => {
    return rows.map((row) => {
      const cells = columns.map((column) => `<td class="${NUMERIC_COLUMNS.has(column) ? 'num' : ''}">${escapeHtml(formatCellValue(row[column], column))}</td>`).join('');
      return `<tr>${cells}</tr>`;
    }).join('');
  };

  const renderSectionTotals = (rows) => {
    const sectionTotals = {
      Amount: rows.reduce((sum, row) => sum + (getNumericValue(row.Amount) ?? 0), 0),
      Applied: rows.reduce((sum, row) => sum + (getNumericValue(row.Applied) ?? 0), 0),
      Balance: rows.reduce((sum, row) => sum + (getNumericValue(row.Balance) ?? 0), 0),
    };

    const cells = columns.map((column, index) => {
      if (index === 0) {
        return '<td><strong>TOTAL</strong></td>';
      }

      if (!NUMERIC_COLUMNS.has(column)) {
        return '<td></td>';
      }

      return `<td class="num"><strong>${escapeHtml(numberFormatter.format(sectionTotals[column] || 0))}</strong></td>`;
    }).join('');

    return `<tr class="total-row">${cells}</tr>`;
  };

  const renderSection = (label, rows) => {
    const body = rows.length > 0
      ? `${renderSectionRows(rows)}${renderSectionTotals(rows)}`
      : `<tr><td colspan="${columns.length}">No ${escapeHtml(label.toLowerCase())} transactions.</td></tr>`;

    return `
      <div class="section">
        <div class="section-header">
          <div class="section-title">${escapeHtml(label)}</div>
          <div class="section-count">${rows.length} transaction${rows.length === 1 ? '' : 's'}</div>
        </div>
        <table>
          <thead>
            <tr>
              ${columns.map((column) => `<th class="${NUMERIC_COLUMNS.has(column) ? 'num' : ''}">${escapeHtml(column)}</th>`).join('')}
            </tr>
          </thead>
          <tbody>
            ${body}
          </tbody>
        </table>
      </div>
    `;
  };

  const allRows = [...groupedRows.unliquidated, ...groupedRows.liquidated];
  const grandTotals = {
    Amount: allRows.reduce((sum, row) => sum + (getNumericValue(row.Amount) ?? 0), 0),
    Applied: allRows.reduce((sum, row) => sum + (getNumericValue(row.Applied) ?? 0), 0),
    Balance: allRows.reduce((sum, row) => sum + (getNumericValue(row.Balance) ?? 0), 0),
  };

  return `
<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>Liquidation Summary Report</title>
    <style>
      @page {
        size: A4 landscape;
        margin: 8mm;
      }
      body {
        margin: 0;
        font-family: Arial, Helvetica, sans-serif;
        color: #111827;
      }
      .report {
        border: 1px solid #d1d5db;
        padding: 16px;
      }
      .head-company {
        font-size: 28px;
        font-weight: 800;
        line-height: 1.2;
      }
      .head-title {
        margin-top: 14px;
        text-align: center;
        font-size: 32px;
        font-weight: 800;
      }
      .head-sub {
        margin-top: 6px;
        text-align: center;
        font-size: 14px;
      }
      .chips {
        margin-top: 12px;
        display: flex;
        gap: 10px;
        font-size: 12px;
      }
      .chip {
        border: 1px solid #9ca3af;
        border-radius: 999px;
        padding: 3px 10px;
      }
      .section {
        margin-top: 14px;
      }
      .section-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        border: 1px solid #d1d5db;
        border-bottom: 0;
        padding: 8px 10px;
        background: #f8fafc;
      }
      .section-title {
        font-size: 16px;
        font-weight: 700;
      }
      .section-count {
        font-size: 13px;
        color: #374151;
      }
      table {
        width: 100%;
        border-collapse: collapse;
        table-layout: fixed;
      }
      th, td {
        border: 1px solid #d1d5db;
        padding: 4px 6px;
        font-size: 12px;
      }
      th {
        text-align: left;
        background: #eff6ff;
      }
      .num {
        text-align: right;
      }
      .total-row td {
        background: #eef2ff;
        font-weight: 700;
      }
      .grand {
        margin-top: 14px;
        display: flex;
        justify-content: flex-end;
        gap: 18px;
        font-size: 14px;
        font-weight: 700;
      }
    </style>
  </head>
  <body>
    <div class="report">
      <div class="head-company">${escapeHtml(companyName || '')}</div>
      <div class="head-title">LIQUIDATION SUMMARY REPORT</div>
      <div class="head-sub">Covering period from ${escapeHtml(formatDateForReport(LIQUIDATION_DETAIL_FROM_DATE))} to ${escapeHtml(formatDateForReport(cutoff))}</div>

      <div class="chips">
        <div class="chip">Trade Name: ${escapeHtml(tradeNameFilter === 'ALL' ? 'All' : tradeNameFilter)}</div>
        <div class="chip">Unliquidated: ${groupedRows.unliquidated.length}</div>
        <div class="chip">Liquidated: ${groupedRows.liquidated.length}</div>
      </div>

      ${renderSection('Unliquidated', groupedRows.unliquidated)}
      ${renderSection('Liquidated', groupedRows.liquidated)}

      <div class="grand">
        <span>Amount: ${escapeHtml(numberFormatter.format(grandTotals.Amount))}</span>
        <span>Applied: ${escapeHtml(numberFormatter.format(grandTotals.Applied))}</span>
        <span>Balance: ${escapeHtml(numberFormatter.format(grandTotals.Balance))}</span>
      </div>
    </div>
  </body>
</html>
  `;
};

const writeToPopupAndPrint = (popup, html) => {
  try {
    popup.document.open();
    popup.document.write(html);
    popup.document.close();
    popup.focus();

    // Give the browser a brief moment to layout the injected document.
    window.setTimeout(() => {
      try {
        popup.print();
      } catch (error) {
        console.error('Print invocation failed:', error);
      }
    }, 120);

    return true;
  } catch (error) {
    console.error('Unable to write printable content to popup:', error);
    return false;
  }
};

const Report_LiquidationReport = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [dateCutoff, setDateCutoff] = useState(getTodayValue());
  const [tradeNameFilter, setTradeNameFilter] = useState('ALL');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rowPrintLoadingId, setRowPrintLoadingId] = useState(null);
  const [error, setError] = useState('');
  const [hasRun, setHasRun] = useState(false);
  const [compID] = useState(() => sessionStorage.getItem('compID'));
  const [compName] = useState(() => sessionStorage.getItem('compName') || sessionStorage.getItem('selectedCompany') || '');

  const rawColumns = useMemo(() => {
    if (!rows.length) {
      return [];
    }

    return Object.keys(rows[0]).filter((column) => column !== 'ID' && column !== 'id');
  }, [rows]);

  const columns = useMemo(() => {
    if (!rawColumns.length) {
      return [];
    }

    const orderedColumns = REPORT_COLUMNS.map((column) => getColumnKey(rawColumns, column)).filter(Boolean);
    //const remainingColumns = rawColumns.filter((column) => !orderedColumns.includes(column));
    //return [...orderedColumns, ...remainingColumns];
    return [...orderedColumns];
  }, [rawColumns]);

  const tradeNameOptions = useMemo(() => {
    const values = new Set();

    rows.forEach((row) => {
      const tradeName = String(row?.TradeName ?? '').trim();
      if (tradeName) {
        values.add(tradeName);
      }
    });

    return ['ALL', ...Array.from(values).sort((left, right) => left.localeCompare(right))];
  }, [rows]);

  const displayRows = useMemo(() => {
    const filteredRows = tradeNameFilter === 'ALL'
      ? rows
      : rows.filter((row) => String(row?.TradeName ?? '').trim() === tradeNameFilter);

    return [...filteredRows].sort((left, right) => {
      const leftStatus = String(left.Status || '').toLowerCase();
      const rightStatus = String(right.Status || '').toLowerCase();

      if (leftStatus !== rightStatus) {
        if (leftStatus === 'unliquidated') return -1;
        if (rightStatus === 'unliquidated') return 1;
      }

      const leftDate = parseDateValue(left.Docdate);
      const rightDate = parseDateValue(right.Docdate);

      if (leftDate && rightDate && leftDate.getTime() !== rightDate.getTime()) {
        return rightDate.getTime() - leftDate.getTime();
      }

      return String(right.Docnumber || '').localeCompare(String(left.Docnumber || ''));
    });
  }, [rows, tradeNameFilter]);

  const groupedRows = useMemo(() => ({
    unliquidated: displayRows.filter((row) => String(row.Status || '').toLowerCase() === 'unliquidated'),
    liquidated: displayRows.filter((row) => String(row.Status || '').toLowerCase() === 'liquidated'),
  }), [displayRows]);

  const totals = useMemo(() => {
    return ['Amount', 'Applied', 'Balance'].reduce((accumulator, column) => {
      accumulator[column] = displayRows.reduce((sum, row) => {
        const value = getNumericValue(row[column]);
        return sum + (value ?? 0);
      }, 0);
      return accumulator;
    }, {});
  }, [displayRows]);

  const liquidatedCount = useMemo(() => displayRows.filter((row) => String(row.Status || '').toLowerCase() === 'liquidated').length, [displayRows]);
  const unliquidatedCount = useMemo(() => displayRows.filter((row) => String(row.Status || '').toLowerCase() === 'unliquidated').length, [displayRows]);

  const summaryCards = [
    { label: 'Transactions', value: displayRows.length, accent: colors.blueAccent?.[500] ?? '#6870fa' },
    { label: 'Unliquidated', value: unliquidatedCount, accent: colors.redAccent?.[400] ?? '#e2726e' },
    { label: 'Liquidated', value: liquidatedCount, accent: colors.greenAccent?.[500] ?? '#4cceac' },
    { label: 'Balance Total', value: numberFormatter.format(totals.Balance || 0), accent: colors.blueAccent?.[300] ?? '#9bb4ff' },
  ];

  const handlePrint = () => {
    if (!displayRows.length) {
      setError('No rows to print. Please run the report first.');
      return;
    }

    const popup = window.open('', '_blank');
    if (!popup) {
      setError('Print popup was blocked by the browser. Please allow popups and try again.');
      return;
    }

    const html = buildLiquidationSummaryPrintHtml({
      companyName: compName,
      cutoff: dateCutoff,
      tradeNameFilter,
      columns,
      groupedRows,
    });

    const wrote = writeToPopupAndPrint(popup, html);
    if (!wrote) {
      popup.close();
      setError('Unable to render printable report in the popup window.');
    }
  };

  const getColumnAlignment = (column) => {
    if (NUMERIC_COLUMNS.has(column)) {
      return 'right';
    }

    if (column === 'Status') {
      return 'center';
    }

    if (/date/i.test(column)) {
      return 'center';
    }

    return 'left';
  };

  const getColumnValue = (row, column) => row[column];

  const runReport = async () => {
    if (!compID) {
      setError('No company is selected in the current session.');
      setRows([]);
      setHasRun(true);
      return;
    }

    if (!dateCutoff) {
      setError('Cutoff date is required.');
      setRows([]);
      setHasRun(true);
      return;
    }

    setLoading(true);
    setError('');
    setHasRun(true);

    try {
      const response = await fetch(`${Connstring2}/api/Reports/Liquidation`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          companyID: compID,
          datecutoff: dateCutoff,
          tradeName: tradeNameFilter === 'ALL' ? '' : tradeNameFilter,
        }),
      });

      const data = await response.json();

      if (!response.ok || !data?.success) {
        setRows([]);
        setError(data?.message || 'Unable to generate liquidation report.');
        return;
      }

      setRows(Array.isArray(data?.rows) ? data.rows : []);
    } catch (requestError) {
      console.error('Error generating liquidation report:', requestError);
      setRows([]);
      setError('Unable to generate liquidation report due to network/server error.');
    } finally {
      setLoading(false);
    }
  };

  const handleExportCsv = () => {
    if (!displayRows.length || !columns.length) {
      return;
    }

    const escapeCsv = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;
    const lines = [columns.map(escapeCsv).join(',')];

    displayRows.forEach((row) => {
      lines.push(columns.map((column) => escapeCsv(getColumnValue(row, column))).join(','));
    });

    lines.push(columns.map((column, index) => {
      if (index === 0) {
        return escapeCsv('TOTAL');
      }

      return NUMERIC_COLUMNS.has(column) ? escapeCsv(totals[column]) : escapeCsv('');
    }).join(','));

    downloadFile(lines.join('\n'), `Liquidation_${dateCutoff}.csv`, 'text/csv;charset=utf-8;');
  };

  const handleExportExcel = () => {
    if (!displayRows.length || !columns.length) {
      return;
    }

    const headerHtml = columns.map((column) => `<th>${column}</th>`).join('');
    const bodyHtml = displayRows.map((row) => `<tr>${columns.map((column) => `<td>${String(getColumnValue(row, column) ?? '')}</td>`).join('')}</tr>`).join('');
    const totalHtml = `<tr>${columns.map((column, index) => {
      if (index === 0) {
        return '<td><strong>TOTAL</strong></td>';
      }

      return NUMERIC_COLUMNS.has(column) ? `<td><strong>${numberFormatter.format(totals[column] || 0)}</strong></td>` : '<td></td>';
    }).join('')}</tr>`;
    const html = `<table><thead><tr>${headerHtml}</tr></thead><tbody>${bodyHtml}${totalHtml}</tbody></table>`;

    downloadFile(`\ufeff${html}`, `Liquidation_${dateCutoff}.xls`, 'application/vnd.ms-excel;charset=utf-8;');
  };

  const handlePrintRow = async (row) => {
    const rowId = row?.ID ?? row?.id;

    if (!rowId) {
      setError('Cannot print this row because it has no ID.');
      return;
    }

    const popup = window.open('', '_blank');
    if (!popup) {
      setError('Print popup was blocked by the browser. Please allow popups and try again.');
      return;
    }

    setRowPrintLoadingId(rowId);
    try {
      const response = await fetch(`${Connstring2}/api/Reports/Liquidation/PrintDetail`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          companyID: compID,
          id: rowId,
          refNo: row?.RefNo ?? row?.Refno ?? '',
          docNumber: row?.Docnumber ?? row?.DocNo ?? '',
          cutoff: dateCutoff,
        }),
      });

      const data = await response.json();
      if (!response.ok || !data?.success) {
        popup.close();
        setError(data?.message || 'Unable to generate row print report.');
        return;
      }

      const detailRows = Array.isArray(data?.rows) ? data.rows : [];
      if (!detailRows.length) {
        popup.close();
        setError('No printable detail rows were returned for this transaction.');
        return;
      }

      const html = buildLiquidationPrintHtml({
        companyName: compName,
        cutoff: dateCutoff,
        sourceRow: row,
        detailRows,
      });

      const wrote = writeToPopupAndPrint(popup, html);
      if (!wrote) {
        popup.close();
        setError('Unable to render row print layout in the popup window.');
      }
    } catch (printError) {
      popup.close();
      console.error('Error generating liquidation print detail:', printError);
      setError('Unable to generate row print report due to network/server error.');
    } finally {
      setRowPrintLoadingId(null);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, minHeight: '100vh', bgcolor: theme.palette.mode === 'dark' ? '#0f172a' : '#f8fafc' }}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2.5, md: 3.5 },
          mb: 3,
          borderRadius: 4,
          background: theme.palette.mode === 'dark'
            ? 'linear-gradient(135deg, rgba(14,116,144,0.95), rgba(59,130,246,0.9))'
            : 'linear-gradient(135deg, #2563eb, #38bdf8)',
          color: '#fff',
          boxShadow: '0 20px 45px rgba(37, 99, 235, 0.24)',
        }}
      >
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }}>
          <Box>
            <Typography variant="h4" fontWeight={700}>
              Liquidation Report
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9, mt: 0.5 }}>
              Transaction summary based on subsidiary ledger liquidation records up to the selected cutoff date.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1.5} flexWrap="wrap">
            <Button
              variant="outlined"
              startIcon={<RefreshOutlinedIcon />}
              onClick={runReport}
              disabled={loading}
              sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.4)', '&:hover': { borderColor: '#fff', backgroundColor: 'rgba(255,255,255,0.12)' } }}
            >
              {loading ? 'Running...' : 'Run Report'}
            </Button>
            <Button
              variant="outlined"
              onClick={handleExportExcel}
              disabled={!displayRows.length || loading}
              sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.4)', '&:hover': { borderColor: '#fff', backgroundColor: 'rgba(255,255,255,0.12)' } }}
            >
              Export Excel
            </Button>
            <Button
              variant="contained"
              onClick={handleExportCsv}
              disabled={!displayRows.length || loading}
              sx={{ bgcolor: '#fff', color: '#2563eb', '&:hover': { bgcolor: '#f8fafc' } }}
            >
              Export CSV
            </Button>
            <Button
              variant="outlined"
              startIcon={<PrintOutlinedIcon />}
              onClick={handlePrint}
              disabled={!displayRows.length || loading}
              sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.4)', '&:hover': { borderColor: '#fff', backgroundColor: 'rgba(255,255,255,0.12)' } }}
            >
              Print
            </Button>
          </Stack>
        </Stack>
      </Paper>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 3 }}>
        {summaryCards.map((card) => (
          <Paper key={card.label} elevation={0} sx={{ flex: 1, p: 2.5, borderRadius: 3, border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(15,23,42,0.06)'}` }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{card.label}</Typography>
            <Typography variant="h5" fontWeight={700} sx={{ color: card.accent }}>
              {card.value}
            </Typography>
          </Paper>
        ))}
      </Stack>

      <Paper elevation={0} sx={{ p: { xs: 2, md: 3 }, mb: 3, borderRadius: 4, border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(15,23,42,0.08)'}` }}>
        <Stack spacing={2}>
          <Typography variant="h6" fontWeight={700}>Parameters</Typography>
          <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} alignItems={{ xs: 'stretch', lg: 'center' }}>
            <TextField
              size="small"
              label="Date Cutoff"
              type="date"
              value={dateCutoff}
              onChange={(event) => setDateCutoff(event.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: { xs: '100%', sm: 180 } }}
            />
            <TextField
              select
              size="small"
              label="Name"
              value={tradeNameFilter}
              onChange={(event) => setTradeNameFilter(event.target.value)}
              sx={{ minWidth: { xs: '100%', sm: 180 } }}
            >
              {tradeNameOptions.map((tradeName) => (
                <MenuItem key={tradeName} value={tradeName}>
                  {tradeName === 'ALL' ? 'All Names' : tradeName}
                </MenuItem>
              ))}
            </TextField>
            <Button variant="contained" startIcon={<AssessmentOutlinedIcon />} onClick={runReport} disabled={loading}>
              {loading ? 'Running...' : 'Generate'}
            </Button>
          </Stack>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip label="Account Code: 4202" color="primary" variant="outlined" />
            <Chip label="Cutoff-driven summary" color="primary" variant="outlined" />
            <Chip label={tradeNameFilter === 'ALL' ? ' Name: All' : ` Name: ${tradeNameFilter}`} color="primary" variant="outlined" />
          </Stack>
          {error && (
            <Typography variant="body2" color="error">{error}</Typography>
          )}
        </Stack>
      </Paper>

      <Paper
        id="liquidation-printable"
        elevation={0}
        sx={{ borderRadius: 4, border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(15,23,42,0.08)'}`, overflow: 'hidden' }}
      >
        <Box sx={{ p: { xs: 2, md: 3 }, borderBottom: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(15,23,42,0.08)'}` }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }}>
            <Box>
              <Box className="liquidation-print-header" sx={{ display: 'none', visibility: 'hidden' }}>{compName ? `${compName} — ` : ''}Liquidation Report</Box>
              <Box className="liquidation-print-meta" sx={{ display: 'none', visibility: 'hidden' }}>Cutoff: {dateCutoff} &nbsp;|&nbsp; Account: 4202</Box>
              <Typography variant="h6" fontWeight={700} className="liquidation-no-print">Report Output</Typography>
              <Typography variant="body2" color="text.secondary" className="liquidation-no-print">
                Summary of liquidation transactions returned by the query.
              </Typography>
            </Box>
            <Chip label={`${displayRows.length} transactions`} color="primary" variant="outlined" className="liquidation-no-print" />
          </Stack>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : !hasRun ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h6" fontWeight={600}>Run the report to view results</Typography>
            <Typography variant="body2" color="text.secondary">Select the cutoff date, then click Generate.</Typography>
          </Box>
        ) : displayRows.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h6" fontWeight={600}>No rows returned</Typography>
            <Typography variant="body2" color="text.secondary">The liquidation query returned no transactions for the selected cutoff date and trade name filter.</Typography>
          </Box>
        ) : (
          <Stack spacing={3}>
            {[
              { key: 'unliquidated', label: 'Unliquidated', color: 'error', rows: groupedRows.unliquidated },
              { key: 'liquidated', label: 'Liquidated', color: 'success', rows: groupedRows.liquidated },
            ].map((section) => (
              <Paper key={section.key} elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(15,23,42,0.08)'}`, overflow: 'hidden' }}>
                <Box sx={{ px: 2.5, py: 2, borderBottom: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(15,23,42,0.08)'}`, backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(15,23,42,0.02)' }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" spacing={1}>
                    <Box>
                      <Typography variant="h6" fontWeight={700}>{section.label}</Typography>
                      <Typography variant="body2" color="text.secondary">{section.rows.length} transaction{section.rows.length === 1 ? '' : 's'}</Typography>
                    </Box>
                    <Chip label={`${section.label}: ${section.rows.length}`} color={section.color} variant="outlined" />
                  </Stack>
                </Box>

                {section.rows.length === 0 ? (
                  <Box sx={{ p: 3, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">No {section.label.toLowerCase()} transactions match the selected agent filter.</Typography>
                  </Box>
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          {columns.map((column) => (
                            <TableCell key={`${section.key}-${column}`} align={getColumnAlignment(column)} sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>{column}</TableCell>
                          ))}
                          <TableCell align="center" sx={{ fontWeight: 700, whiteSpace: 'nowrap' }} className="liquidation-no-print">Action</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {section.rows.map((row, rowIndex) => (
                          <TableRow key={`${section.key}-row-${rowIndex}`} hover>
                            {columns.map((column) => (
                              <TableCell key={`${section.key}-${column}-${rowIndex}`} align={getColumnAlignment(column)} sx={{ whiteSpace: 'nowrap' }}>
                                {column === 'Status' ? (
                                  <Chip size="small" label={String(row[column] || '—')} color={section.color} variant="outlined" />
                                ) : (
                                  formatCellValue(getColumnValue(row, column), column)
                                )}
                              </TableCell>
                            ))}
                            <TableCell align="center" className="liquidation-no-print" sx={{ whiteSpace: 'nowrap' }}>
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<PrintOutlinedIcon />}
                                onClick={() => handlePrintRow(row)}
                                disabled={rowPrintLoadingId === (row?.ID ?? row?.id)}
                              >
                                {rowPrintLoadingId === (row?.ID ?? row?.id) ? 'Printing...' : 'Print'}
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                        <TableRow className="liquidation-total-row" sx={{ '& td': { fontWeight: 700, backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(37,99,235,0.06)' } }}>
                          {columns.map((column, index) => (
                            <TableCell key={`${section.key}-total-${column}`} align={getColumnAlignment(column)} sx={{ whiteSpace: 'nowrap' }}>
                              {index === 0 ? 'TOTAL' : NUMERIC_COLUMNS.has(column) ? numberFormatter.format(section.rows.reduce((sum, row) => sum + (getNumericValue(row[column]) ?? 0), 0)) : ''}
                            </TableCell>
                          ))}
                          <TableCell className="liquidation-no-print" />
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Paper>
            ))}
          </Stack>
        )}
      </Paper>
    </Box>
  );
};

export default Report_LiquidationReport;
